import { jsxs, jsx, Fragment } from "react/jsx-runtime";
import * as React from "react";
import { CloseIcon } from "./internal.js";
import { ToastProvider as ToastProvider$1 } from "../vendor/@base-ui/react/toast/provider/ToastProvider.js";
import { ToastPortal } from "../vendor/@base-ui/react/toast/portal/ToastPortal.js";
import { ToastViewport } from "../vendor/@base-ui/react/toast/viewport/ToastViewport.js";
import { useToastManager } from "../vendor/@base-ui/react/toast/useToastManager.js";
import { ToastRoot } from "../vendor/@base-ui/react/toast/root/ToastRoot.js";
import { ToastTitle } from "../vendor/@base-ui/react/toast/title/ToastTitle.js";
import { ToastDescription } from "../vendor/@base-ui/react/toast/description/ToastDescription.js";
import { ToastAction } from "../vendor/@base-ui/react/toast/action/ToastAction.js";
import { ToastClose } from "../vendor/@base-ui/react/toast/close/ToastClose.js";
const ToastContext = React.createContext(null);
const TONE_ICON = { success: "✓", info: "i", warning: "!", danger: "!" };
function createToastApi(add, close) {
  const show = ({ title, description, tone = "info", timeout, action, id, onClose }) => {
    let toastId = id;
    const actionProps = action ? {
      children: action.label,
      onClick: () => {
        action.onClick();
        if (toastId) close(toastId);
      }
    } : void 0;
    toastId = add({
      id,
      title,
      description,
      type: tone,
      priority: tone === "danger" ? "high" : "low",
      timeout,
      onClose,
      actionProps
    });
    return toastId;
  };
  return {
    show,
    success: (title, options) => show({ ...options, title, tone: "success" }),
    info: (title, options) => show({ ...options, title, tone: "info" }),
    warning: (title, options) => show({ ...options, title, tone: "warning" }),
    danger: (title, options) => show({ ...options, title, tone: "danger" }),
    dismiss: (id) => close(id)
  };
}
function ToastApiProvider({ children }) {
  const { add, close } = useToastManager();
  const api = React.useMemo(() => createToastApi(add, close), [add, close]);
  return /* @__PURE__ */ jsx(ToastContext.Provider, { value: api, children });
}
function isTone(value) {
  return value === "success" || value === "info" || value === "warning" || value === "danger";
}
function ToastItem({ toast, closeLabel }) {
  const tone = isTone(toast.type) ? toast.type : "info";
  return /* @__PURE__ */ jsxs(ToastRoot, { toast, className: "ev-toast", "data-tone": tone, swipeDirection: ["left", "right"], children: [
    /* @__PURE__ */ jsx("span", { className: "ev-toast__icon", "aria-hidden": "true", children: TONE_ICON[tone] }),
    /* @__PURE__ */ jsxs("div", { className: "ev-toast__body", children: [
      /* @__PURE__ */ jsx(ToastTitle, { className: "ev-toast__title", render: /* @__PURE__ */ jsx("div", {}) }),
      /* @__PURE__ */ jsx(ToastDescription, { className: "ev-toast__desc" })
    ] }),
    toast.actionProps ? /* @__PURE__ */ jsx(ToastAction, { className: "ev-btn ev-btn--ghost ev-toast__action" }) : null,
    /* @__PURE__ */ jsx(ToastClose, { className: "ev-btn ev-btn--ghost ev-btn--icon ev-toast__close", "aria-label": closeLabel, children: /* @__PURE__ */ jsx(CloseIcon, {}) })
  ] });
}
function ToastList({ closeLabel }) {
  const { toasts } = useToastManager();
  return /* @__PURE__ */ jsx(Fragment, { children: toasts.slice().reverse().map((toast) => /* @__PURE__ */ jsx(ToastItem, { toast, closeLabel }, toast.id)) });
}
function ToastProvider({
  children,
  corner = "bottom-right",
  timeout = 5e3,
  limit = 3,
  label = "Notifications",
  closeLabel = "Dismiss notification",
  container
}) {
  return /* @__PURE__ */ jsxs(ToastProvider$1, { timeout, limit, children: [
    /* @__PURE__ */ jsx(ToastApiProvider, { children }),
    /* @__PURE__ */ jsx(ToastPortal, { container, children: /* @__PURE__ */ jsx(ToastViewport, { className: "ev-toasts ev-root", "data-corner": corner, "aria-label": label, children: /* @__PURE__ */ jsx(ToastList, { closeLabel }) }) })
  ] });
}
function useToast() {
  const api = React.useContext(ToastContext);
  if (!api) throw new Error("useToast() must be used inside <ToastProvider>.");
  return api;
}
export {
  ToastProvider,
  useToast
};
//# sourceMappingURL=Toast.js.map
